import { styled } from '@mui/material/styles';
import * as Yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { Typography, TextField, Button as MuiButton, FormControl, Grow } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation } from 'react-router-dom';

export type ForgotPasswordFormData = {
  email: string;
};

const ForgotPasswordSchema = Yup.object().shape({
  email: Yup.string().required('Please enter your email address').email('Enter a valid email'),
});

const ForgotPasswordForm = ({ onSubmit, loading }) => {
  const { state } = useLocation();
  const { handleSubmit, control } = useForm<ForgotPasswordFormData>({
    mode: 'onSubmit',
    resolver: yupResolver(ForgotPasswordSchema),
    defaultValues: {
      email: state?.email || '',
    },
  });

  return (
    <Grow in style={{ transformOrigin: 'top center' }}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Title>Enter your email</Title>
        <Description>We'll send you a verification code to reset your password.</Description>
        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <TextField
                {...field}
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState?.error?.message || ''}
                variant="outlined"
                placeholder="Email"
                autoFocus
              />
            </FormControl>
          )}
          name="email"
        />

        <Button fullWidth variant="contained" disabled={loading} type="submit">
          {loading ? 'Loading...' : 'Submit'}
        </Button>
      </Form>
    </Grow>
  );
};

const Form = styled('form', { label: 'Form' })(({ theme }) => ({
  maxWidth: 500,
  width: '100%',
  padding: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.custom.black,
      },
    },
  },
}));

const Title = styled(Typography, { label: 'Title' })(({ theme }) => ({
  fontSize: 36,
  fontWeight: 700,
  color: theme.palette.custom.mirage,
  marginBottom: 0,
  [theme.breakpoints.down('md')]: {
    fontSize: 24,
  },
}));

const Description = styled(Typography, { label: 'Description' })({
  fontSize: 18,
  margin: '8px 0 40px',
});

const Button = styled(MuiButton, { label: 'Button' })({
  borderRadius: 8,
  height: 56,
});

export default ForgotPasswordForm;
